import React, { useCallback, useEffect, useState } from "react";
import {
  Building2,
  Plus,
  Power,
  Users,
  ClipboardList,
  LogIn,
  Undo2,
  Pencil,
  Link2,
  Hash,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../api/axios";
import useAuthStore from "../store/useAuthStore";
import { inicialesDe } from "../utils/marca";
import Header from "./Header";
import { Ventana, Aviso } from "./modulos/base";
import { plural } from "../utils/texto";

/**
 * Panel del SUPERADMINISTRADOR: las residenciales de la plataforma.
 *
 * Es la única pantalla que ve más de una residencial. Desde aquí se dan de
 * alta —con su primer administrador y su configuración de fábrica en el mismo
 * paso, porque una residencial sin administrador no sirve de nada—, se edita
 * su marca y se entra a cualquiera para dar soporte.
 *
 * Un administrador de residencial NO llega aquí: la ruta exige el rol de
 * plataforma y el servidor lo vuelve a comprobar en cada petición.
 *
 * ── COLORES: por qué no hay ni un `var(--x, #fff)` en este archivo ──────────
 *
 * Esta pantalla salió ilegible en producción —botones en forma de óvalo vacío,
 * tarjetas blancas con texto gris claro en tema oscuro— porque pedía unas
 * variables `--surface` y `--surface-muted` que NO EXISTEN en el sistema de
 * diseño. El respaldo del `var(..., #fff)` ganaba siempre, así que la tarjeta
 * era blanca en los dos temas y nadie se enteraba: no da error, sólo deja de
 * leerse. Las variables de verdad son `--bg-elevated`, `--bg-tertiary`,
 * `--text` y `--text-secondary` (ver `src/index.css`).
 *
 * Por eso aquí se usan **sin respaldo literal**: si mañana alguien escribe mal
 * el nombre de una variable, el color se rompe de forma visible en el momento
 * en vez de quedarse en blanco para siempre.
 *
 * ── Y por qué cada botón y cada campo declaran su `color` ───────────────────
 *
 * `public/index.html` lleva `<meta name="color-scheme" content="light dark">`.
 * Un `<button>` o un `<input>` que no fije su color lo toma del navegador
 * según la preferencia del SISTEMA, no según el tema de la aplicación: con el
 * sistema en oscuro y la tarjeta blanca, el texto del botón salía casi blanco
 * sobre blanco. De ahí los óvalos vacíos.
 */

/*
 * Valor con el que arranca el selector de color cuando la residencial todavía
 * no tiene uno. Es un DATO que se va a guardar en la residencial —no un color
 * de la interfaz—, y va escrito porque un `<input type="color">` no acepta
 * `var(--gold)` como valor. Coincide a propósito con `--gold`.
 */
const COLOR_POR_DEFECTO = "#D4AF37";

const esHex = (v) => /^#[0-9a-fA-F]{6}$/.test(String(v || "").trim());

const Residenciales = () => {
  const { residentialSeleccionada, seleccionarResidencial } = useAuthStore();
  const [residenciales, setResidenciales] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    polygons: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
  });

  // Edición de una residencial ya existente (su marca, su contacto).
  const [editando, setEditando] = useState(null);
  const [edicion, setEdicion] = useState(null);
  const [errorEdicion, setErrorEdicion] = useState("");
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [logoRoto, setLogoRoto] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      /* Con las inactivas. El servidor las esconde por defecto
         (`residentialController.js:77`), y este panel es el único sitio desde
         el que se puede volver a activar una: sin este parámetro, desactivar
         era una puerta de un solo sentido —la residencial desaparecía de la
         lista y el botón «Activar» no podía dibujarse nunca—. */
      const { data } = await api.get("/residentials", {
        params: { incluirInactivas: "true" },
      });
      setResidenciales(data.residenciales || []);
    } catch {
      // El interceptor ya avisó al usuario.
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crear = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post("/residentials", {
        name: form.name,
        phone: form.phone,
        address: form.address,
        polygons: form.polygons
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean),
        admin: {
          name: form.adminName,
          email: form.adminEmail,
          password: form.adminPassword,
        },
      });
      toast.success("Residencial creada");
      setCreando(false);
      setForm({
        name: "",
        phone: "",
        address: "",
        polygons: "",
        adminName: "",
        adminEmail: "",
        adminPassword: "",
      });
      cargar();
    } catch {
      /* avisado por el interceptor */
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (r) => {
    try {
      await api.patch(`/residentials/${r._id}/estado`, { activa: !r.isActive });
      toast.success(r.isActive ? "Residencial desactivada" : "Residencial activada");
      cargar();
    } catch {
      /* avisado por el interceptor */
    }
  };

  /**
   * Entra o sale del contexto de una residencial.
   *
   * El rótulo NO dice «Salir»: el único «Salir» de la aplicación es el de la
   * cabecera, que cierra la sesión. Tenerlos iguales hacía que quien sólo
   * quería dejar de mirar una residencial se arriesgara a cerrar la sesión.
   */
  const entrar = (r) => {
    const dentro = residentialSeleccionada === r._id;
    seleccionarResidencial(dentro ? null : r._id);
    toast.info(dentro ? `Ya no está trabajando en ${r.name}` : `Trabajando en ${r.name}`);
  };

  const abrirEdicion = (r) => {
    setEditando(r);
    setEdicion({
      name: r.name || "",
      phone: r.phone || "",
      email: r.email || "",
      address: r.address || "",
      logoUrl: r.logoUrl || "",
      primaryColor: r.primaryColor || "",
      polygons: (r.polygons || []).join(", "),
    });
    setErrorEdicion("");
    setLogoRoto(false);
  };

  const cerrarEdicion = useCallback(() => {
    setEditando(null);
    setEdicion(null);
    setErrorEdicion("");
    setGuardandoEdicion(false);
  }, []);

  const cambiarEdicion = (clave, valor) => {
    setEdicion((actual) => ({ ...actual, [clave]: valor }));
    if (clave === "logoUrl") setLogoRoto(false);
  };

  /**
   * Guarda la marca y el contacto con `PUT /residentials/:id`.
   *
   * El código (`RES001`) y el enlace público NO se mandan: el servidor no los
   * acepta a propósito —soporte usa el primero y el segundo puede estar
   * impreso en los avisos de la residencial—, así que tampoco se ofrecen.
   */
  const guardarEdicion = async (e) => {
    e.preventDefault();
    if (!edicion.name.trim()) {
      setErrorEdicion("El nombre no puede quedar vacío.");
      return;
    }
    setGuardandoEdicion(true);
    setErrorEdicion("");
    try {
      await api.put(`/residentials/${editando._id}`, {
        name: edicion.name.trim(),
        phone: edicion.phone,
        email: edicion.email,
        address: edicion.address,
        logoUrl: edicion.logoUrl,
        primaryColor: edicion.primaryColor,
        polygons: edicion.polygons
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean),
      });
      toast.success("Residencial actualizada");
      cerrarEdicion();
      cargar();
    } catch (err) {
      // El mensaje del servidor, tal cual: es el que dice qué campo falló.
      setErrorEdicion(
        err?.response?.data?.message || "No se pudo guardar la residencial"
      );
      setGuardandoEdicion(false);
    }
  };

  const campo = (etiqueta, clave, extra = {}) => (
    <label style={estilos.campo}>
      <span style={estilos.etiqueta}>{etiqueta}</span>
      <input
        style={estilos.input}
        value={form[clave]}
        onChange={(e) => setForm({ ...form, [clave]: e.target.value })}
        {...extra}
      />
    </label>
  );

  /* Campos del formulario de edición. La etiqueta se asocia con `htmlFor`/`id`
     y no envolviendo el campo, porque la fila del color lleva DOS controles
     dentro y un `<label>` con dos campos no tiene nombre accesible claro. */
  const campoEdicion = (etiqueta, clave, extra = {}) => (
    <div style={estilos.campo}>
      <label style={estilos.etiqueta} htmlFor={`editar-${clave}`}>
        {etiqueta}
      </label>
      <input
        id={`editar-${clave}`}
        style={estilos.input}
        value={edicion[clave]}
        onChange={(e) => cambiarEdicion(clave, e.target.value)}
        {...extra}
      />
    </div>
  );

  return (
    <>
      <Header />
      <div style={estilos.contenedor}>
      <header style={estilos.cabecera}>
        <div>
          <h1 style={estilos.titulo}>Residenciales</h1>
          <p style={estilos.subtitulo}>
            Cada una es independiente: sus residentes, sus guardias, sus visitas y su
            configuración. Nada se comparte entre ellas.
          </p>
        </div>
        <button type="button" style={estilos.botonPrimario} onClick={() => setCreando((v) => !v)}>
          <Plus size={18} /> Nueva residencial
        </button>
      </header>

      {creando && (
        <form style={estilos.formulario} onSubmit={crear}>
          <h2 style={estilos.tituloSeccion}>Datos de la residencial</h2>
          <div style={estilos.rejilla}>
            {campo("Nombre *", "name", { required: true, minLength: 3, maxLength: 120 })}
            {campo("Teléfono", "phone", { maxLength: 20 })}
            {campo("Dirección", "address", { maxLength: 250 })}
            {campo("Polígonos (separados por coma)", "polygons", {
              placeholder: "A, B, C",
            })}
          </div>

          <h2 style={estilos.tituloSeccion}>Primer administrador</h2>
          <p style={estilos.ayuda}>
            Es quien dará de alta a los residentes y guardias. Se crea junto con la
            residencial porque sin él nadie podría entrar a configurarla.
          </p>
          <div style={estilos.rejilla}>
            {campo("Nombre *", "adminName", { required: true })}
            {campo("Correo *", "adminEmail", { required: true, type: "email" })}
            {campo("Contraseña *", "adminPassword", {
              required: true,
              type: "password",
              minLength: 6,
              placeholder: "Letras y números, mínimo 6",
            })}
          </div>

          <div style={estilos.acciones}>
            <button type="button" style={estilos.botonSecundario} onClick={() => setCreando(false)}>
              Cancelar
            </button>
            <button type="submit" style={estilos.botonPrimario} disabled={guardando}>
              {guardando ? "Creando…" : "Crear residencial"}
            </button>
          </div>
        </form>
      )}

      {cargando ? (
        <p style={estilos.vacio}>Cargando residenciales…</p>
      ) : residenciales.length === 0 ? (
        <p style={estilos.vacio}>Todavía no hay ninguna residencial.</p>
      ) : (
        <div style={estilos.lista}>
          {residenciales.map((r) => {
            const dentro = residentialSeleccionada === r._id;
            return (
            <article
              key={r._id}
              style={{
                ...estilos.tarjeta,
                ...(dentro ? estilos.tarjetaActiva : {}),
                ...(r.isActive ? {} : estilos.tarjetaInactiva),
              }}
            >
              <div style={estilos.logo}>
                {r.logoUrl ? (
                  <img src={r.logoUrl} alt="" style={estilos.logoImagen} />
                ) : (
                  <span>{inicialesDe(r.name)}</span>
                )}
              </div>

              <div style={estilos.datos}>
                <h3 style={estilos.nombre}>
                  {r.name} <span style={estilos.codigo}>{r.code}</span>
                </h3>
                <p style={estilos.cifras}>
                  <Users size={14} /> {r.usuarios} usuarios
                  <span style={estilos.separador}>·</span>
                  <ClipboardList size={14} /> {r.visitas} {plural(r.visitas, "visita")}
                  {!r.isActive && <span style={estilos.inactiva}>Desactivada</span>}
                </p>
              </div>

              <div style={estilos.botones}>
                <button
                  type="button"
                  style={dentro ? estilos.botonAcento : estilos.botonSecundario}
                  onClick={() => entrar(r)}
                  title={
                    dentro
                      ? "Volver a ver toda la plataforma"
                      : "Trabajar dentro de esta residencial"
                  }
                >
                  {dentro ? <Undo2 size={16} /> : <LogIn size={16} />}
                  {dentro ? "Dejar de trabajar aquí" : "Trabajar aquí"}
                </button>
                <button
                  type="button"
                  style={estilos.botonSecundario}
                  onClick={() => abrirEdicion(r)}
                  title="Cambiar su nombre, su logo, su color y sus datos de contacto"
                >
                  <Pencil size={16} />
                  Editar
                </button>
                <button
                  type="button"
                  style={estilos.botonSecundario}
                  onClick={() => cambiarEstado(r)}
                  title={
                    r.isActive
                      ? "Desactivar: sus usuarios dejan de entrar, los datos se conservan"
                      : "Activar de nuevo"
                  }
                >
                  <Power size={16} />
                  {r.isActive ? "Desactivar" : "Activar"}
                </button>
              </div>
            </article>
            );
          })}
        </div>
      )}

      <p style={estilos.nota}>
        <Building2 size={14} /> Desactivar una residencial no borra nada: sus usuarios
        dejan de poder entrar y su historial queda intacto.
      </p>
      </div>

      {/* ── Edición ──────────────────────────────────────────────────────────
          La marca (nombre, logo y color) es lo primero que ve un cliente: el
          servidor la manda al iniciar sesión y con ella se pinta toda la
          interfaz de esa residencial. Sin esta ventana no había forma de
          ponerla. */}
      <Ventana
        titulo={editando ? `Editar ${editando.name}` : "Editar residencial"}
        abierta={Boolean(editando && edicion)}
        alCerrar={cerrarEdicion}
        pie={
          <>
            <button type="button" style={estilos.botonSecundario} onClick={cerrarEdicion}>
              Cancelar
            </button>
            <button
              type="submit"
              form="form-editar-residencial"
              style={estilos.botonPrimario}
              disabled={guardandoEdicion}
            >
              {guardandoEdicion ? "Guardando…" : "Guardar cambios"}
            </button>
          </>
        }
      >
        {edicion && (
          <form id="form-editar-residencial" style={estilos.formEdicion} onSubmit={guardarEdicion}>
            {errorEdicion && <Aviso>{errorEdicion}</Aviso>}

            {/* Solo lectura, y a propósito: el código lo usa soporte y el
                enlace público puede estar impreso en los avisos. */}
            <p style={estilos.soloLectura}>
              <span style={estilos.soloLecturaDato}>
                <Hash size={14} /> {editando.code}
              </span>
              <span style={estilos.soloLecturaDato}>
                <Link2 size={14} /> /{editando.slug}
              </span>
              <span style={estilos.ayuda}>No se pueden cambiar</span>
            </p>

            <div style={estilos.rejilla}>
              {campoEdicion("Nombre *", "name", { required: true, maxLength: 120 })}
              {campoEdicion("Teléfono", "phone", { maxLength: 20 })}
              {campoEdicion("Correo", "email", { type: "email", maxLength: 120 })}
              {campoEdicion("Dirección", "address", { maxLength: 250 })}
              {campoEdicion("Polígonos (separados por coma)", "polygons", {
                placeholder: "A, B, C",
              })}
            </div>

            <h3 style={estilos.tituloSeccion}>Marca</h3>
            <p style={estilos.ayuda}>
              Es lo que ven sus residentes y sus visitantes al entrar. Se envía al
              iniciar sesión y con ella se pinta su interfaz.
            </p>

            <div style={estilos.campo}>
              <label style={estilos.etiqueta} htmlFor="editar-logoUrl">
                Logo (dirección de la imagen)
              </label>
              <div style={estilos.filaDoble}>
                <input
                  id="editar-logoUrl"
                  style={estilos.inputFila}
                  value={edicion.logoUrl}
                  onChange={(e) => cambiarEdicion("logoUrl", e.target.value)}
                  placeholder="https://…"
                  maxLength={500}
                />
                {/* Si la imagen no carga no se dibuja nada: un ícono roto
                    hace creer que el sistema falló cuando lo que está mal es
                    la dirección que acaban de pegar. */}
                {edicion.logoUrl.trim() && !logoRoto && (
                  <img
                    src={edicion.logoUrl.trim()}
                    alt="Vista previa del logo"
                    style={estilos.vistaPrevia}
                    onError={() => setLogoRoto(true)}
                  />
                )}
              </div>
            </div>

            <div style={estilos.campo}>
              <label style={estilos.etiqueta} htmlFor="editar-primaryColor">
                Color principal
              </label>
              <div style={estilos.filaDoble}>
                {/* El selector y el texto son dos campos a propósito: el
                    selector es para elegir y el texto para PEGAR el valor
                    exacto de la marca, que es como llega de un diseñador. */}
                <input
                  type="color"
                  id="editar-primaryColorSelector"
                  aria-label="Elegir el color principal"
                  style={estilos.selectorColor}
                  value={esHex(edicion.primaryColor) ? edicion.primaryColor.trim() : COLOR_POR_DEFECTO}
                  onChange={(e) => cambiarEdicion("primaryColor", e.target.value)}
                />
                <input
                  id="editar-primaryColor"
                  style={estilos.inputFila}
                  value={edicion.primaryColor}
                  onChange={(e) => cambiarEdicion("primaryColor", e.target.value)}
                  placeholder={COLOR_POR_DEFECTO}
                  maxLength={20}
                />
              </div>
            </div>
          </form>
        )}
      </Ventana>
    </>
  );
};

/*
 * Todas las variables son las que `src/index.css` define de verdad y van SIN
 * respaldo literal: ver la nota del encabezado.
 */
const estilos = {
  contenedor: { padding: "2rem", maxWidth: 1100, margin: "0 auto" },
  cabecera: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "1rem",
    flexWrap: "wrap",
    marginBottom: "1.5rem",
  },
  titulo: { margin: 0, fontSize: "1.6rem", color: "var(--text)" },
  subtitulo: { margin: "0.35rem 0 0", color: "var(--text-secondary)", maxWidth: 560 },
  tituloSeccion: { fontSize: "1rem", margin: "1rem 0 0.5rem", color: "var(--text)" },
  ayuda: { margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--text-secondary)" },
  formulario: {
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: "1.25rem",
    marginBottom: "1.5rem",
    background: "var(--bg-elevated)",
    color: "var(--text)",
  },
  formEdicion: { display: "grid", gap: "0.75rem" },
  rejilla: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: "0.75rem" },
  campo: { display: "flex", flexDirection: "column", gap: "0.25rem" },
  etiqueta: { fontSize: "0.8rem", color: "var(--text-secondary)" },
  /* El `color` y el `background` son obligatorios: sin ellos el navegador los
     toma de la preferencia del sistema y los campos salían como bloques gris
     oscuro sobre la tarjeta blanca. 44 px de alto es la medida del proyecto. */
  input: {
    minHeight: 44,
    padding: "0.6rem 0.75rem",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    color: "var(--text)",
    fontSize: "0.95rem",
  },
  filaDoble: { display: "flex", alignItems: "center", gap: "0.5rem" },
  /* El mismo campo, pero compartiendo fila con el selector de color o con la
     vista previa del logo. `minWidth: 0` evita que el campo empuje a su
     compañero fuera de la ventana en el teléfono. */
  inputFila: {
    flex: 1,
    minWidth: 0,
    minHeight: 44,
    padding: "0.6rem 0.75rem",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    color: "var(--text)",
    fontSize: "0.95rem",
  },
  selectorColor: {
    width: 48,
    height: 44,
    flexShrink: 0,
    padding: 2,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    cursor: "pointer",
  },
  vistaPrevia: {
    width: 44,
    height: 44,
    flexShrink: 0,
    objectFit: "cover",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
  },
  soloLectura: {
    margin: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.75rem",
    fontSize: "0.82rem",
    color: "var(--text-secondary)",
  },
  soloLecturaDato: { display: "inline-flex", alignItems: "center", gap: "0.3rem" },
  acciones: { display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" },
  lista: { display: "grid", gap: "0.75rem" },
  tarjeta: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "1rem",
    borderRadius: 16,
    border: "1px solid var(--border)",
    background: "var(--bg-elevated)",
    color: "var(--text)",
    flexWrap: "wrap",
  },
  tarjetaActiva: { borderColor: "var(--gold)", boxShadow: "0 0 0 1px var(--gold) inset" },
  /* Una residencial desactivada se marca con el fondo y el borde, NO bajando
     la opacidad: con `opacity` el texto deja de cumplir el contraste mínimo y
     el dato que se quería atenuar es justo el que hay que poder leer. */
  tarjetaInactiva: { background: "var(--bg-secondary)", borderStyle: "dashed" },
  logo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "var(--bg-tertiary)",
    color: "var(--text)",
    fontWeight: 600,
  },
  logoImagen: { width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 },
  datos: { flex: 1, minWidth: 200 },
  nombre: {
    margin: 0,
    fontSize: "1.05rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "var(--text)",
  },
  codigo: { fontSize: "0.75rem", color: "var(--text-secondary)", fontWeight: 400 },
  cifras: {
    margin: "0.3rem 0 0",
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    fontSize: "0.85rem",
    color: "var(--text-secondary)",
  },
  separador: { margin: "0 0.35rem" },
  /* `--warning-text` se descartó: en tema claro su naranja (#E65100) da 3.5:1
     sobre la tarjeta y no llega al mínimo de 4.5:1 para texto de este tamaño.
     `--danger-*` da ~6:1 en los DOS temas, y además es honesto: con la
     residencial desactivada sus usuarios no pueden entrar. */
  inactiva: {
    marginLeft: "0.5rem",
    padding: "0.1rem 0.5rem",
    borderRadius: 999,
    border: "1px solid var(--danger)",
    background: "var(--danger-bg)",
    color: "var(--danger-text)",
    fontWeight: 600,
  },
  botones: { display: "flex", gap: "0.5rem", flexWrap: "wrap" },
  botonPrimario: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.6rem 1rem",
    minHeight: 44,
    borderRadius: 999,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "var(--midnight)",
    fontWeight: 600,
    cursor: "pointer",
  },
  botonSecundario: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.55rem 0.9rem",
    minHeight: 44,
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "var(--bg-tertiary)",
    color: "var(--text)",
    fontWeight: 600,
    cursor: "pointer",
  },
  /* El botón de la residencial en la que se está trabajando: dorado por el
     borde y el fondo suave, con el texto en `--text` para que se lea igual en
     los dos temas. */
  botonAcento: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.55rem 0.9rem",
    minHeight: 44,
    borderRadius: 999,
    border: "1px solid var(--gold)",
    background: "var(--gold-subtle)",
    color: "var(--text)",
    fontWeight: 600,
    cursor: "pointer",
  },
  vacio: { color: "var(--text-secondary)", padding: "2rem 0", textAlign: "center" },
  nota: {
    marginTop: "1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    fontSize: "0.85rem",
    color: "var(--text-secondary)",
  },
};

export default Residenciales;
